import {
  DataViewOption,
  DataViewProps,
  DataViewToggle,
  DataViewTogglesState,
  VisualizationOption,
  isNestedVisualizationOption,
} from '@va/types/report-block';
import { LocalStorage } from '@va/util/helpers';
import { StorageKeysManager } from '@va/util/misc';
import { useReportBlockConfig } from '@va/ui/components/report-block';
import { useCallback, useEffect, useState } from 'react';

function createViewKey(identifier: string, namespace: string) {
  return StorageKeysManager.getDataViewKey({ namespace, identifier });
}

function createTogglesKey(identifier: string, namespace: string) {
  return StorageKeysManager.getDataViewTogglesKey({ namespace, identifier });
}

function getStoredView(identifier: string, namespace: string, options?: VisualizationOption[]) {
  const value = LocalStorage.getItem(createViewKey(identifier, namespace));
  if (!value) return undefined;
  if (!Object.values(DataViewOption).includes(value as DataViewOption)) {
    return undefined;
  }
  if (options) {
    const available = options.flatMap((opt) =>
      isNestedVisualizationOption(opt) ? opt.options : [opt as DataViewOption],
    );
    if (!available.includes(value as DataViewOption)) return undefined;
  }

  return value as DataViewOption;
}

function getInitialToggles(
  identifier: string,
  options: VisualizationOption[] | undefined,
  namespace: string,
): DataViewTogglesState {
  let stored: DataViewTogglesState = {};
  const result: DataViewTogglesState = {};
  if (!options) return result;

  try {
    const raw = LocalStorage.getItem(createTogglesKey(identifier, namespace));
    if (raw) {
      stored = JSON.parse(raw);
    }
  } catch (error) {
    console.error('Error while parsing toggle state:', error);
  }

  for (const opt of options) {
    if (!isNestedVisualizationOption(opt) || !opt.toggles) continue;

    for (const toggle of opt.toggles) {
      if (!result[opt.view]) result[opt.view] = {};

      result[opt.view]![toggle.id as DataViewToggle] =
        stored[opt.view]?.[toggle.id as DataViewToggle] ?? toggle.defaultValue ?? true;
    }
  }

  return result;
}

export const useDataView = ({
  defaultView,
  identifier,
  options,
}: {
  defaultView: DataViewOption;
  options?: VisualizationOption[];
  identifier: string;
}): DataViewProps => {
  const { storageNamespace } = useReportBlockConfig();

  const [view, setView] = useState<DataViewOption>(getStoredView(identifier, storageNamespace, options) || defaultView);
  const [viewToggles, setViewToggles] = useState<DataViewTogglesState>(() =>
    getInitialToggles(identifier, options, storageNamespace),
  );

  // Re-initialize when the namespace changes (e.g. website switch)
  useEffect(() => {
    setView(getStoredView(identifier, storageNamespace, options) || defaultView);
    setViewToggles(getInitialToggles(identifier, options, storageNamespace));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageNamespace]);

  const onViewChange = useCallback(
    (view: DataViewOption) => {
      setView(view);
      LocalStorage.setItem(createViewKey(identifier, storageNamespace), view);
    },
    [identifier, storageNamespace],
  );

  const onViewToggleChange = useCallback(
    (id: DataViewToggle, view: DataViewOption, value: boolean) => {
      setViewToggles((prev) => {
        const next = { ...prev, [view]: { ...prev[view], [id]: value } };
        LocalStorage.setItem(createTogglesKey(identifier, storageNamespace), JSON.stringify(next));
        return next;
      });
    },
    [identifier, storageNamespace],
  );

  const getToggleState = useCallback(
    (toggle: DataViewToggle, selectedView?: DataViewOption): boolean => {
      const lookupView =
        selectedView ??
        options?.filter(isNestedVisualizationOption).find((opt) => opt.options.includes(view))?.view ??
        view;
      return viewToggles?.[lookupView]?.[toggle] ?? false;
    },
    [view, viewToggles, options],
  );

  return {
    viewOptions: options || [],
    onViewChange,
    selectedView: view,
    viewToggles,
    onViewToggleChange,
    getToggleState,
  };
};
